'use client';

import React, { SyntheticEvent, useRef, useState } from 'react';
import { useVirtual } from 'react-virtual';
import { createGlobalStyle } from 'styled-components';
import { Box, BoxProps } from '../../wag-react/next-components/box';
import { HealthArticleSectionAdvice, HealthArticleSectionAdviceValue } from '../HealthArticleSectionAdvice';
import { HealthArticleSectionHealthExperience, HealthArticleSectionHealthExperienceValue } from '../HealthArticleSectionHealthExperience';
import { VirtualItemWrapper } from './components/VirtualItemWrapper';
export type HealthArticleUGCVirtualListValue = (HealthArticleSectionAdviceValue | HealthArticleSectionHealthExperienceValue)[];
export type HealthArticleUGCVirtualListProps = BoxProps & {
  items: HealthArticleUGCVirtualListValue;
  adviceProps: Record<'onClickYes' | 'onClickNo', (event: SyntheticEvent<HTMLButtonElement>, advice: HealthArticleSectionAdviceValue) => void>;
};

// Hide the scrollbar to avoid unexpected UI issue with it
const GlobalStyle = createGlobalStyle`
  .virtual-list-container::-webkit-scrollbar {
    /* Chrome, Safari */
    display: none;
  }
  .virtual-list-container {
    /* Firefox */
    scrollbar-width: none;
  }
`;
export const HealthArticleUGCVirtualList = ({
  items,
  adviceProps: {
    onClickYes,
    onClickNo
  },
  children,
  ...rest
}: HealthArticleUGCVirtualListProps) => {
  const parentRef = useRef();

  /**
   * Set a container height that will only be evaluated ONCE during the lifetime
   * of this component. This doesn't change on succeeding render of this component
   *
   * It is important to determine the height of the container
   * ONCE to avoid CLS issue
   *
   * 400px is the minimum height per item
   */
  const [containerHeight] = useState(() => items.length ? `${items.length * 400}px` : 0);
  const rowVirtualizer = useVirtual({
    size: items.length,
    parentRef
  });
  return <>
      <GlobalStyle data-sentry-element="GlobalStyle" data-sentry-source-file="index.tsx" />
      <Box className="virtual-list-container" ref={parentRef} height={containerHeight} width="100%" overflow="auto" my="40px" {...rest} data-sentry-element="Box" data-sentry-source-file="index.tsx">
        <Box height={`${rowVirtualizer.totalSize}px`} width={1} position="relative" data-sentry-element="Box" data-sentry-source-file="index.tsx">
          {rowVirtualizer.virtualItems.map(virtualRow => {
          const item = items[virtualRow.index];
          // This will narrow the type for TypeScript when using the `in` operator
          if ('responses' in item) {
            return <VirtualItemWrapper key={item.id} virtualRow={virtualRow}>
                  <HealthArticleSectionAdvice value={item} onClickYes={event => onClickYes(event, item)} onClickNo={event => onClickNo(event, item)} />
                </VirtualItemWrapper>;
          }
          return <VirtualItemWrapper key={String(item?.question?.text)} virtualRow={virtualRow}>
                <HealthArticleSectionHealthExperience value={item} />
              </VirtualItemWrapper>;
        })}
        </Box>
        {children}
      </Box>
    </>;
};